import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, IconButton, InputAdornment, Tooltip, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import SmartMateriaUITable from '../../../generic/SmartMateriaUITable'
import InfoIcon from '@mui/icons-material/Info'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TextSnippetOutlined from '@mui/icons-material/TextSnippetOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import MosSmartDialog from '../common/MosSmartDialog'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import AlertUI from '../../../generic/AlertUI'
import InspectionReportWidgetToolbar from './InspectionReportWidgetToolbar'
import { FeatureIndicatorSlider } from './FeatureIndicatorSlider'
import FeatureForm from '../../../configuration/workflow2.0/feature/FeatureForm'
import SmartTextField from '../../../generic/smarttextfield/SmartTextField'
import { DateTimePicker } from '@mui/x-date-pickers'
import SmartSelect from '../../../generic/smartSelect/SmartSelect'
import { useToggle } from '@mantine/hooks'
import Document from '../../../configuration/document/Document'

const isPartialNumber = (value) => value === '' || /^-?\d*\.?\d*$/.test(value)

// A feature counts as reported when the operator has entered a value; NO_RESPONSE
// features are informational and are always considered resolved.
const isFeatureReported = (feature) => {
  if (feature.featureType?.dataType === 'NO_RESPONSE') {
    return true
  }
  const value = feature.measuredValue
  return value !== null && value !== undefined && value !== ''
}

// Whether the operator actually entered a measured value (excludes informational NO_RESPONSE).
const hasMeasuredValue = (feature) => {
  if (feature.featureType?.dataType === 'NO_RESPONSE') {
    return false
  }
  const value = feature.measuredValue
  return value !== null && value !== undefined && value !== ''
}

// `features` is a list of feature groups: { id, code, name, description, features: [FeatureDto] }.
const InspectionReportDetail = ({ features, acceptRef, alert, getDocumentTempUUID }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [openDetail, setOpenDetail] = React.useState(false)
  const [selectedRow, setSelectedRow] = React.useState(null)
  const [tableData, setTableData] = useState([])
  // Group metadata kept alongside the flat tableData so the editing/validation logic stays
  // group-agnostic while each group is rendered in its own collapsible section.
  const [groups, setGroups] = useState([])
  const [toogleErrors, changeToolgeErrors] = useToggle()
  const [openDocuments, setOpenDocuments] = useState(false)
  const [documentRow, setDocumentRow] = useState(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)

  const allToolRefs = useMemo(() => {
    if (!tableData) {
      return []
    }
    return tableData.flatMap((item) => item.toolReferences)
  }, [tableData])

  const allUnits = useMemo(() => {
    if (!tableData) {
      return []
    }
    return tableData.filter((item) => item).map((item) => item.unit)
  }, [tableData])

  useEffect(() => {
    const clonedGroups = structuredClone(features || [])
    const flatFeatures = []
    const groupMeta = []
    clonedGroups.forEach((group) => {
      const groupFeatures = group.features || []
      groupFeatures.forEach((item) => {
        if (item.featureType?.dataType === 'DATE' && item.measuredValue) {
          item.measuredValue = new Date(item.measuredValue)
        }
        flatFeatures.push(item)
      })
      groupMeta.push({
        id: group.id,
        code: group.code,
        name: group.name,
        description: group.description,
        featureIds: groupFeatures.map((item) => item.id)
      })
    })
    setTableData(flatFeatures)
    setGroups(groupMeta)
  }, [features])

  useEffect(() => {
    if (alert) {
      showAlert(alert)
    }
  }, [alert, showAlert])

  const onChangeMeassuredValue = useCallback((row, newValue) => {
    setTableData((oldData) => {
      const newData = [...oldData]
      const findRow = newData.find((item) => item.id === row.id)
      findRow.measuredValue = newValue
      return newData
    })
    changeToolgeErrors()
  }, [changeToolgeErrors])

  const onChangeNumericValue = useCallback((row, newValue) => {
    if (!isPartialNumber(newValue)) {
      return
    }
    onChangeMeassuredValue(row, newValue)
  }, [onChangeMeassuredValue])

  // Increments/decrements the stored numeric value by delta (empty value is treated as 0).
  const onStepNumericValue = useCallback((row, delta) => {
    setTableData((oldData) => {
      const newData = [...oldData]
      const findRow = newData.find((item) => item.id === row.id)
      const current = parseFloat(findRow.measuredValue)
      const base = Number.isFinite(current) ? current : 0
      findRow.measuredValue = String(base + delta)
      return newData
    })
    changeToolgeErrors()
  }, [changeToolgeErrors])

  const renderNumericField = useCallback((row, item, width) => (
    <SmartTextField
      style={{ minWidth: width }}
      value={item.measuredValue ?? ''}
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{
        endAdornment: (
          <InputAdornment position='end'>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton size='small' sx={{ p: 0, height: 16 }} onClick={() => onStepNumericValue(row, 1)}>
                <KeyboardArrowUpIcon fontSize='small' />
              </IconButton>
              <IconButton size='small' sx={{ p: 0, height: 16 }} onClick={() => onStepNumericValue(row, -1)}>
                <KeyboardArrowDownIcon fontSize='small' />
              </IconButton>
            </Box>
          </InputAdornment>
        )
      }}
      onChange={(value) => onChangeNumericValue(row, value)}
    />
  ), [onChangeNumericValue, onStepNumericValue])

  const onChangeSerialNumber = useCallback((row, newValue) => {
    setTableData((oldData) => {
      const newData = [...oldData]
      const findRow = newData.find((item) => item.id === row.id)
      findRow.serialNumber = newValue
      return newData
    })
  }, [])

  const onChangeResponsible = useCallback((row, newValue) => {
    setTableData((oldData) => {
      const newData = [...oldData]
      const findRow = newData.find((item) => item.id === row.id)
      findRow.responsible = newValue
      return newData
    })
  }, [])

  const requiresSerialNumber = useCallback((row) => {
    return (row.toolReferences && row.toolReferences.filter((item) => item.serializable).length > 0)
  }, [])

  // Per-group view derived live from tableData: which features belong to each group and whether
  // the group is fully reported. The completion state is surfaced in the (possibly collapsed)
  // section header so the operator knows if a group still needs input without expanding it.
  const groupSections = useMemo(() => {
    return groups.map((group) => {
      const groupFeatures = group.featureIds
        .map((featureId) => tableData.find((feature) => feature.id === featureId))
        .filter(Boolean)
      const total = groupFeatures.length
      const filled = groupFeatures.filter(isFeatureReported).length
      const missingSerial = groupFeatures.some(
        (feature) => requiresSerialNumber(feature) && hasMeasuredValue(feature) && !feature.serialNumber
      )
      const title = group.code && group.name
        ? group.code + ' · ' + group.name
        : (group.name || group.code || '')
      return {
        ...group,
        features: groupFeatures,
        total,
        filled,
        missingSerial,
        complete: total > 0 && filled === total && !missingSerial,
        title
      }
    })
  }, [groups, tableData, requiresSerialNumber])

  const getGroupStatus = (section) => {
    if (section.complete) {
      return { icon: <CheckCircleOutlineIcon color='success' />, tooltip: t_('All features reported'), chipColor: 'success' }
    }
    if (section.missingSerial) {
      return { icon: <WarningAmberIcon color='warning' />, tooltip: t_('Missing serial number'), chipColor: 'warning' }
    }
    return { icon: <ErrorOutlineIcon color='warning' />, tooltip: t_('Pending features'), chipColor: 'warning' }
  }

  const columns = useMemo(() => [
    {
      name: t_('Order'),
      field: 'featureOrder',
      readOnly: true
    },
    {
      field: 'code',
      name: t_('Feature code'),
      readOnly: true
    }, {
      field: 'name',
      name: t_('Feature name'),
      readOnly: true
    },
    {
      field: 'featureType.name',
      name: t_('Type'),
      readOnly: true
    },
    {
      field: 'qualitativeAttribute',
      name: t_('Question'),
      readOnly: true,
      secondaryLine: true,
      secondaryLineCellStyle: { paddingTop: 0, paddingBottom: '0.75rem' },
      render: (row) => row.qualitativeAttribute
        ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', pl: 1, whiteSpace: 'normal' }}>
            <Typography
              sx={{ color: 'primary.main', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}
            >
              {t_('Question')}:
            </Typography>
            <Typography sx={{ lineHeight: 1.4 }}>{row.qualitativeAttribute}</Typography>
          </Box>
          )
        : null
    },
    {
      field: 'value',
      name: t_('Nominal'),
      readOnly: true
    },
    {
      field: 'upperTolerance',
      name: t_('Upper tolerance'),
      readOnly: true,
      render: (row) => {
        if (row.featureType.dataType !== 'RANGE') {
          return null
        }
        return row.upperTolerance
      }
    },
    {
      field: 'lowerTolerance',
      name: t_('Lower tolerance'),
      readOnly: true,
      render: (row) => {
        if (row.featureType.dataType !== 'RANGE') {
          return null
        }
        return row.lowerTolerance
      }
    },
    {
      field: 'measuredValue',
      name: t_('Measured value'),
      render: (row) => {
        const width = '12rem'
        const item = tableData.find((item) => item.id === row.id)
        if (!item) {
          return null
        }

        // NO_RESPONSE features are informational and expect no answer from the operator.
        if (row.featureType.dataType === 'NO_RESPONSE') {
          return (
            <Typography variant='body2' color='text.secondary' sx={{ minWidth: width }}>
              {t_('No response required')}
            </Typography>
          )
        }

        if (row.featureType.dataType === 'QUANTITATIVE') {
          return renderNumericField(row, item, width)
        }
        if (row.featureType.dataType === 'DATE') {
          return (
            <DateTimePicker
              value={item.measuredValue}
              onChange={(value) => {
                onChangeMeassuredValue(row, value)
              }}
              sx={{ minWidth: width }}
            />
          )
        }

        if (row.featureType.dataType === 'BOOLEAN') {
          const okString = t_('OK')
          const noOkString = t_('NOK')
          const value = item.measuredValue === null ? null : item.measuredValue === 'true' ? okString : noOkString
          return (
            <SmartSelect
              value={value}
              selectableOptions={[okString, noOkString]}
              onChange={(value) => onChangeMeassuredValue(row, value === null ? null : value === okString ? 'true' : 'false')}
            />
          )
        }

        if (row.featureType.dataType === 'RANGE') {
          return renderNumericField(row, item, width)
        }

        return (
          <SmartTextField
            style={{ minWidth: width }}
            value={item.measuredValue}
            onChange={(value) => onChangeMeassuredValue(row, value)}
          />
        )
      }

    },
    {
      name: t_('Unit'),
      field: 'unit.code',
      readOnly: true,
      type: 'smartSelect',
      edit: {
        source: async () => {
          return Promise.resolve(allUnits)
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Deviation'),
      field: '',
      render: (row) => {
        if (row.featureType.dataType !== 'RANGE' || !row.lowerTolerance || !row.upperTolerance || !row.value) {
          return null
        }
        const item = tableData.find((item) => item.id === row.id)
        if (!item) {
          return null
        }
        return <FeatureIndicatorSlider nominalValue={row.value} lowerTolerance={row.lowerTolerance} upperTolerance={row.upperTolerance} value={item.measuredValue} />
      }
    },
    {
      name: t_('Inspection Tool'),
      field: 'toolReferences.name',
      readOnly: true,
      type: 'smartMultiSelect',
      edit: {
        source: async () => {
          return Promise.resolve(allToolRefs)
        },
        id: 'id',
        field: 'name'
      },
      width: 'max-content'
    },
    {
      name: t_('Serial Number'),
      field: 'serialNumber',
      render: (row) => {
        if (!requiresSerialNumber(row)) {
          return null
        }
        const item = tableData.find((item) => item.id === row.id)
        if (!item) {
          return null
        }
        return (
          <SmartTextField
            forceCheckError={toogleErrors}
            checkError={(value) => item.measuredValue && !value ? t_('Required') : ''}
            value={item.serialNumber}
            onChange={(value) => onChangeSerialNumber(row, value)}
            style={{ minWidth: '10rem' }}
          />
        )
      }
    },

    {
      field: 'inspectionPoint.name',
      name: t_('Inspection point'),
      readOnly: true
    },

    {
      name: t_('Responsible'),
      field: 'responsible',
      render: (row) => {
        const item = tableData.find((item) => item.id === row.id)
        if (!item) {
          return null
        }
        return (
          <SmartTextField
            value={item.responsible}
            onChange={(value) => onChangeResponsible(row, value)}
            style={{ minWidth: '10rem' }}
          />
        )
      }
    }
  ], [allToolRefs, allUnits, onChangeMeassuredValue, renderNumericField, onChangeSerialNumber, requiresSerialNumber, onChangeResponsible, t_, tableData, toogleErrors])
  const actions = {
    custom: [
      {
        name: 'Info',
        contextMenu: false,
        render: (row) => {
          return (
            <IconButton onClick={() => { setSelectedRow(row); setOpenDetail(true) }}>
              <InfoIcon />
            </IconButton>
          )
        }
      },
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => { setDocumentRow(row); setOpenDocuments(true) }
      }
    ]
  }

  const validate = () => {
    const filledItems = tableData.filter(feature => feature.measuredValue !== null && feature.measuredValue !== '')

    // Check if any is invalid
    for (const item of filledItems) {
      if (requiresSerialNumber(item) && !item.serialNumber) {
        return false
      }
    }

    // only creates a record for them when they actually have documents.
    const noResponseItems = tableData.filter(feature => feature.featureType?.dataType === 'NO_RESPONSE')

    return [...filledItems, ...noResponseItems]
      .map(item => ({
        uuid: item.id,
        value: item.featureType?.dataType === 'NO_RESPONSE' ? null : item.measuredValue,
        serialNumber: item.serialNumber,
        responsible: item.responsible,
        documentTempUUID: getDocumentTempUUID(item.id)
      }))
  }
  React.useImperativeHandle(acceptRef, () => ({ validate }))

  const sortOptions = {
    default: {
      field: 'featureOrder',
      direction: 1
    }
  }

  return (
    <>
      {AlertElement}
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: t_('Feature Detail') })}
        setOpen={openDetail}
        closeCallback={() => setOpenDetail(false)}
        close
        accept={false}
        cancel={false}
      >
        <FeatureForm
          editingFeature={selectedRow}
          disabled
        />
      </MosSmartDialog>
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: t_('Documents'), icon: TextSnippetOutlined })}
        setOpen={openDocuments}
        closeCallback={() => setOpenDocuments(false)}
        close
        accept={false}
        cancel={false}
        disableButtons={isDownloadingFile}
      >
        {documentRow
          ? (
            <Document
              resourceType='INSPECTION_REPORT_FEATURE'
              reference={{ id: documentRow.latestInspectionReportFeatureId || undefined, name: documentRow.code }}
              referenceTempUUID={getDocumentTempUUID(documentRow.id)}
              referenceUUID={getDocumentTempUUID(documentRow.id)}
              isDownloadingFile={isDownloadingFile}
              setIsDownloadingFile={setIsDownloadingFile}
            />
            )
          : null}
      </MosSmartDialog>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ px: 1 }}>
          <InspectionReportWidgetToolbar />
        </Box>
        {groupSections.length === 0
          ? <Typography variant='body2' color='text.secondary' sx={{ px: 1 }}>{t_('No data to display')}</Typography>
          : groupSections.map((section) => {
            const status = getGroupStatus(section)
            return (
              <Accordion
                key={section.id}
                defaultExpanded
                disableGutters
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: (theme) => theme.palette.background.color_373636,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1, minWidth: 0 }}>
                    <Tooltip title={status.tooltip}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>{status.icon}</Box>
                    </Tooltip>
                    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                      <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {section.title}
                      </Typography>
                      {section.description
                        ? <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>{section.description}</Typography>
                        : null}
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Chip
                      size='small'
                      variant='outlined'
                      color={status.chipColor}
                      label={section.filled + '/' + section.total}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <SmartMateriaUITable
                    columns={columns}
                    actions={actions}
                    dataFetch={section.features}
                    setMultiEdit={setTableData}
                    sortOptions={sortOptions}
                    sticky={false}
                    toolbarDisplay='none'
                    searchAndPaginationVisible='none'
                  />
                </AccordionDetails>
              </Accordion>
            )
          })}
      </Box>
    </>
  )
}

export default InspectionReportDetail
